import { NoPropertySelected } from "@/components/admin/NoPropertySelected";
import { BrandLoader } from "@/components/brand/BrandLoader";
import { EmptyState } from "@/components/brand/EmptyState";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { reservationService, roomService, roomTypeService } from "@/lib/api";
import {
  type RoomBoardStatus,
  groupRoomsByFloor,
  roomBoardCell,
  todayLocalISO,
  unassignedReservations,
} from "@/lib/room-board";
import { useSelectedProperty } from "@/stores/property";
import type { Room } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { GripVertical, Search, StickyNote } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

// ─── Presentation maps ───────────────────────────────────────────────────────

const STATUS_UI: Record<RoomBoardStatus, { label: string; card: string; badge: string }> = {
  vacant: { label: "공실", card: "border-border bg-card", badge: "bg-muted text-muted-foreground" },
  reserved: {
    label: "예약",
    card: "border-blue-300 bg-blue-50",
    badge: "bg-blue-100 text-blue-700",
  },
  occupied: {
    label: "재실",
    card: "border-green-300 bg-green-50",
    badge: "bg-green-100 text-green-700",
  },
  checkout_due: {
    label: "퇴실 임박",
    card: "border-amber-400 bg-amber-50",
    badge: "bg-amber-100 text-amber-700",
  },
  checkout_over: {
    label: "퇴실 초과",
    card: "border-red-400 bg-red-50 animate-pulse",
    badge: "bg-red-100 text-red-600",
  },
  unavailable: {
    label: "사용불가",
    card: "border-dashed border-border bg-muted opacity-60",
    badge: "bg-muted text-muted-foreground",
  },
};

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("en-CA");
}

function usePersistentToggle(key: string, initial: boolean) {
  const [value, setValue] = useState(() => {
    const raw = localStorage.getItem(key);
    return raw == null ? initial : raw === "1";
  });
  const set = (v: boolean) => {
    setValue(v);
    localStorage.setItem(key, v ? "1" : "0");
  };
  return [value, set] as const;
}

function errorDetail(error: unknown, fallback: string): string {
  const detail = isAxiosError(error) ? error.response?.data?.detail : null;
  return typeof detail === "string" ? detail : fallback;
}

// ─── Walk-in dialog ──────────────────────────────────────────────────────────

interface WalkInDialogProps {
  room: Room | null;
  today: string;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (guestName: string, checkOut: string) => void;
}

function WalkInDialog({ room, today, isPending, onOpenChange, onConfirm }: WalkInDialogProps) {
  const [guestName, setGuestName] = useState("");
  const [checkOut, setCheckOut] = useState(addDays(today, 1));

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setGuestName("");
      setCheckOut(addDays(today, 1));
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={room != null} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>워크인 체크인 — {room?.name}</DialogTitle>
          <DialogDescription>
            예약 없이 방문한 게스트를 {today}부터 이 호실에 바로 체크인합니다.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="walkin-name">게스트명 *</Label>
            <Input
              id="walkin-name"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="홍길동"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="walkin-checkout">체크아웃 *</Label>
            <Input
              id="walkin-checkout"
              type="date"
              min={addDays(today, 1)}
              value={checkOut}
              onChange={(e) => setCheckOut(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            취소
          </Button>
          <Button
            type="button"
            disabled={!guestName.trim() || checkOut <= today || isPending}
            onClick={() => onConfirm(guestName.trim(), checkOut)}
          >
            워크인 체크인
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function RoomBoardPage() {
  const queryClient = useQueryClient();
  const propertyId = useSelectedProperty();
  const today = todayLocalISO();

  const [showTimes, setShowTimes] = usePersistentToggle("room-board:show-times", true);
  const [groupFloors, setGroupFloors] = usePersistentToggle("room-board:group-floors", true);
  const [trayQuery, setTrayQuery] = useState("");
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [walkInRoom, setWalkInRoom] = useState<Room | null>(null);
  const [memoDraft, setMemoDraft] = useState("");
  const [dragOverRoomId, setDragOverRoomId] = useState<string | null>(null);

  const { data: rooms = [], isLoading: loadingRooms } = useQuery({
    queryKey: ["rooms", propertyId],
    queryFn: () => roomService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });
  const { data: reservations = [], isLoading: loadingReservations } = useQuery({
    queryKey: ["reservations", propertyId],
    queryFn: () => reservationService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });
  const { data: roomTypes = [] } = useQuery({
    queryKey: ["room-types", propertyId],
    queryFn: () => roomTypeService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["reservations"] });
    queryClient.invalidateQueries({ queryKey: ["rooms"] });
    queryClient.invalidateQueries({ queryKey: ["room-type-availability"] });
  };

  // ── Mutations ─────────────────────────────────────────────────────────────
  const assignMutation = useMutation({
    mutationFn: ({ id, room_id }: { id: string; room_id: string | null }) =>
      reservationService.assignRoom(id, room_id),
    onSuccess: (_, { room_id }) => {
      invalidate();
      toast.success(room_id ? "호실이 배정되었습니다." : "배정이 해제되었습니다.");
    },
    onError: (error) => toast.error(errorDetail(error, "배정에 실패했습니다.")),
  });

  const checkInMutation = useMutation({
    mutationFn: (id: string) => reservationService.checkIn(id),
    onSuccess: () => {
      invalidate();
      toast.success("체크인 처리되었습니다.");
    },
    onError: (error) => toast.error(errorDetail(error, "체크인에 실패했습니다.")),
  });

  const checkOutMutation = useMutation({
    mutationFn: (id: string) => reservationService.checkOut(id),
    onSuccess: () => {
      invalidate();
      toast.success("체크아웃 처리되었습니다.");
    },
    onError: () => toast.error("체크아웃에 실패했습니다."),
  });

  const memoMutation = useMutation({
    mutationFn: ({ id, memo }: { id: string; memo: string | null }) =>
      roomService.update(id, { memo }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rooms"] });
      toast.success("객실 메모가 저장되었습니다.");
    },
    onError: () => toast.error("메모 저장에 실패했습니다."),
  });

  const walkInMutation = useMutation({
    mutationFn: async ({
      room,
      guestName,
      checkOut,
    }: {
      room: Room;
      guestName: string;
      checkOut: string;
    }) => {
      const res = await reservationService.create({
        property_id: propertyId ?? "",
        room_type_id: room.room_type_id ?? "",
        check_in: today,
        check_out: checkOut,
        guest_name: guestName,
      });
      await reservationService.assignRoom(res.id, room.id);
      return reservationService.checkIn(res.id);
    },
    onSuccess: () => {
      invalidate();
      setWalkInRoom(null);
      toast.success("워크인 체크인 처리되었습니다.");
    },
    onError: (error) => toast.error(errorDetail(error, "워크인 등록에 실패했습니다.")),
  });

  // ── Drag & drop assignment ────────────────────────────────────────────────
  const handleDrop = (room: Room, e: React.DragEvent) => {
    e.preventDefault();
    setDragOverRoomId(null);
    const resId = e.dataTransfer.getData("text/plain");
    const res = reservations.find((r) => r.id === resId);
    if (!res) return;
    if (res.room_type_id !== room.room_type_id) {
      toast.error("객실 타입이 다른 호실에는 배정할 수 없습니다.");
      return;
    }
    assignMutation.mutate({ id: res.id, room_id: room.id });
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const roomTypeMap = new Map(roomTypes.map((rt) => [rt.id, rt.name]));
  const unassigned = unassignedReservations(reservations, trayQuery);
  const floorGroups: [string, Room[]][] = groupFloors
    ? groupRoomsByFloor(rooms)
    : rooms.length > 0
      ? [["전체", rooms]]
      : [];

  const selectedRoom = rooms.find((r) => r.id === selectedRoomId) ?? null;
  const selectedCell = selectedRoom ? roomBoardCell(selectedRoom, reservations, today) : null;

  const openRoom = (r: Room) => {
    setSelectedRoomId(r.id);
    setMemoDraft(r.memo ?? "");
  };

  if (!propertyId) {
    return <NoPropertySelected />;
  }

  const isLoading = loadingRooms || loadingReservations;

  return (
    <div className="space-y-6">
      {/* Header + display toggles */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">객실현황판</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            전 객실 상태를 한눈에 보고 카드에서 체크인·아웃을 바로 처리합니다.
          </p>
        </div>
        <div className="flex items-center gap-5 text-sm">
          <label className="flex items-center gap-2">
            <Switch checked={showTimes} onCheckedChange={setShowTimes} />
            일정 표시
          </label>
          <label className="flex items-center gap-2">
            <Switch checked={groupFloors} onCheckedChange={setGroupFloors} />층 나누기
          </label>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {(Object.keys(STATUS_UI) as RoomBoardStatus[]).map((s) => (
          <span key={s} className="flex items-center gap-1.5">
            <span className={`inline-block h-3 w-3 rounded-sm border ${STATUS_UI[s].card}`} />
            {STATUS_UI[s].label}
          </span>
        ))}
      </div>

      {/* Room grid */}
      {isLoading ? (
        <BrandLoader className="py-12" size={44} label="불러오는 중…" />
      ) : rooms.length === 0 ? (
        <Card>
          <EmptyState
            title="등록된 호실이 없어요"
            description="호실을 먼저 등록하면 객실현황판에 카드가 표시됩니다."
            action={
              <Button asChild>
                <Link to="/admin/rooms">호실 관리로 이동</Link>
              </Button>
            }
          />
        </Card>
      ) : (
        floorGroups.map(([floor, floorRooms]) => (
          <section key={floor} className="space-y-2">
            {groupFloors ? (
              <h2 className="text-sm font-medium text-muted-foreground">{floor}층</h2>
            ) : null}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {floorRooms.map((r) => {
                const cell = roomBoardCell(r, reservations, today);
                const ui = STATUS_UI[cell.status];
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => openRoom(r)}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverRoomId(r.id);
                    }}
                    onDragLeave={() => setDragOverRoomId((cur) => (cur === r.id ? null : cur))}
                    onDrop={(e) => handleDrop(r, e)}
                    className={`rounded-lg border-2 p-3 text-left transition-shadow hover:shadow-md ${ui.card} ${
                      dragOverRoomId === r.id ? "ring-2 ring-primary" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <span className="font-medium">{r.name}</span>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ui.badge}`}
                      >
                        {ui.label}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-xs text-muted-foreground">
                      {r.room_type_id ? (roomTypeMap.get(r.room_type_id) ?? "-") : "타입 없음"}
                    </p>
                    {cell.reservation ? (
                      <div className="mt-2 space-y-0.5 text-xs">
                        <p className="truncate font-medium">{cell.reservation.guest_name}</p>
                        {showTimes ? (
                          <p className="tabular-nums text-muted-foreground">
                            {cell.reservation.check_in} ~ {cell.reservation.check_out}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    {r.memo ? (
                      <p className="mt-2 flex items-center gap-1 truncate text-xs text-amber-700">
                        <StickyNote className="h-3 w-3 shrink-0" />
                        {r.memo}
                      </p>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </section>
        ))
      )}

      {/* Unassigned tray */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base">
              미배정 예약 <span className="text-muted-foreground">({unassigned.length})</span>
            </CardTitle>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={trayQuery}
                onChange={(e) => setTrayQuery(e.target.value)}
                placeholder="이름·예약번호 검색"
                className="h-9 w-56 pl-8"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            카드를 같은 타입의 호실로 드래그하면 체크인 전 호실이 배정됩니다.
          </p>
        </CardHeader>
        <CardContent>
          {unassigned.length === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">미배정 예약이 없습니다.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {unassigned.map((rsv) => (
                <div
                  key={rsv.id}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/plain", rsv.id)}
                  className="flex cursor-grab items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm shadow-sm active:cursor-grabbing"
                >
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{rsv.guest_name}</p>
                    <p className="text-xs tabular-nums text-muted-foreground">
                      {roomTypeMap.get(rsv.room_type_id) ?? "-"} · {rsv.check_in} ~ {rsv.check_out}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Room detail / actions dialog */}
      <Dialog
        open={selectedRoom != null}
        onOpenChange={(open) => {
          if (!open) setSelectedRoomId(null);
        }}
      >
        <DialogContent>
          {selectedRoom && selectedCell ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selectedRoom.name}
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_UI[selectedCell.status].badge}`}
                  >
                    {STATUS_UI[selectedCell.status].label}
                  </span>
                </DialogTitle>
                <DialogDescription>
                  {selectedRoom.room_type_id
                    ? (roomTypeMap.get(selectedRoom.room_type_id) ?? "-")
                    : "타입 없음"}
                  {selectedRoom.floor ? ` · ${selectedRoom.floor}층` : ""}
                </DialogDescription>
              </DialogHeader>

              {selectedCell.reservation ? (
                <div className="rounded-md border bg-muted/40 p-3 text-sm">
                  <p className="font-medium">{selectedCell.reservation.guest_name}</p>
                  <p className="mt-0.5 tabular-nums text-muted-foreground">
                    {selectedCell.reservation.check_in} ~ {selectedCell.reservation.check_out}
                  </p>
                </div>
              ) : null}

              <div className="flex flex-wrap gap-2">
                {selectedCell.reservation?.status === "booked" ? (
                  <>
                    <Button
                      size="sm"
                      disabled={checkInMutation.isPending}
                      onClick={() => checkInMutation.mutate(selectedCell.reservation?.id ?? "")}
                    >
                      체크인
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={assignMutation.isPending}
                      onClick={() =>
                        assignMutation.mutate({
                          id: selectedCell.reservation?.id ?? "",
                          room_id: null,
                        })
                      }
                    >
                      배정 해제
                    </Button>
                  </>
                ) : null}
                {selectedCell.reservation?.status === "checked_in" ? (
                  <Button
                    size="sm"
                    disabled={checkOutMutation.isPending}
                    onClick={() => checkOutMutation.mutate(selectedCell.reservation?.id ?? "")}
                  >
                    체크아웃
                  </Button>
                ) : null}
                {selectedCell.status === "vacant" && selectedRoom.room_type_id ? (
                  <Button size="sm" onClick={() => setWalkInRoom(selectedRoom)}>
                    워크인 체크인
                  </Button>
                ) : null}
                <Button size="sm" variant="outline" asChild>
                  <Link to="/admin/reservations">예약 목록</Link>
                </Button>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="room-memo">객실 메모</Label>
                <Textarea
                  id="room-memo"
                  value={memoDraft}
                  onChange={(e) => setMemoDraft(e.target.value)}
                  placeholder="예: 에어컨 수리 예정, 어메니티 보충 필요…"
                  rows={3}
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={memoMutation.isPending || memoDraft === (selectedRoom.memo ?? "")}
                    onClick={() =>
                      memoMutation.mutate({
                        id: selectedRoom.id,
                        memo: memoDraft.trim() || null,
                      })
                    }
                  >
                    메모 저장
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <WalkInDialog
        room={walkInRoom}
        today={today}
        isPending={walkInMutation.isPending}
        onOpenChange={(open) => {
          if (!open) setWalkInRoom(null);
        }}
        onConfirm={(guestName, checkOut) => {
          if (!walkInRoom) return;
          walkInMutation.mutate({ room: walkInRoom, guestName, checkOut });
        }}
      />
    </div>
  );
}
